/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { FC, memo } from "react";
import { createUseStyles } from "react-jss";
import ClickOutsideListener from "HOC/ClickOutsideListener";

import { areEqualShallow } from "Utils/areEqual";
import CaretDown from "Assets/images/caret_down.svg";
import styles from "./styles";

interface Props {
  isStartingPoint: boolean;
  placeholder: string;
  inputValue: string;
  hoverFocusedSuggestion: string;
  suggestionMenu: boolean;
  filteredSuggestion: any[];
  onKeyUp: (e: any) => void;
  clickOutside: () => void;
  onClick: (e: any) => void;
  onFocus: (e: any) => void;
  onChange: (e: any) => void;
  inputRef: any;
  dropDown: boolean;
  onDropdownClick: () => void;
  suggestions: any[];
  directionsRef: any;
}

const Autocomplete: FC<Props> = memo(
  ({
    isStartingPoint,
    placeholder,
    inputValue,
    hoverFocusedSuggestion = "",
    suggestionMenu,
    filteredSuggestion,
    onKeyUp,
    clickOutside,
    onClick,
    onChange,
    inputRef,
    onFocus,
    dropDown,
    onDropdownClick,
    suggestions,
    directionsRef,
  }: Props) => {
    const useStyles = createUseStyles(styles);
    const classes = useStyles();

    let suggestionsListComponent: JSX.Element;
    if (dropDown) {
      suggestionsListComponent = (
        <ul
          className={`hasMatches ${classes.suggestions}`}
          data-testid="suggestions"
          onKeyUp={onKeyUp}
          id={`suggestions-menu-${
            isStartingPoint ? "starting-point" : "destination"
          }`}
          role="listbox"
        >
          {suggestions.map((suggestion) => {
            const { title } = suggestion;
            return (
              <button
                id={title}
                role="option"
                className={`${classes.button} ${
                  classes.suggestionsHoverFocus
                } ${
                  title.toLocaleLowerCase() ===
                  hoverFocusedSuggestion.toLocaleLowerCase()
                    ? classes.addKeyNavFocus
                    : ""
                }`}
                key={title}
                onClick={onClick}
                aria-live="assertive"
                aria-label={title}
                aria-selected={
                  title.toLocaleLowerCase() ===
                  hoverFocusedSuggestion.toLocaleLowerCase()
                }
              >
                <span>{title}</span>
              </button>
            );
          })}
        </ul>
      );
    }
    if (suggestionMenu && inputValue) {
      if (filteredSuggestion.length) {
        const value = new RegExp(inputValue, "i");
        suggestionsListComponent = (
          <ul
            className={`hasMatches ${classes.suggestions}`}
            data-testid="suggestions"
            onKeyUp={onKeyUp}
            id={`suggestions-menu-${
              isStartingPoint ? "starting-point" : "destination"
            }`}
            role="listbox"
          >
            {filteredSuggestion.map((suggestion) => {
              const { title } = suggestion;
              return (
                <button
                  id={title}
                  role="option"
                  className={`${classes.button} ${
                    classes.suggestionsHoverFocus
                  } ${
                    title.toLocaleLowerCase() ===
                    hoverFocusedSuggestion.toLocaleLowerCase()
                      ? classes.addKeyNavFocus
                      : ""
                  }`}
                  key={title}
                  onClick={onClick}
                  aria-label={title}
                  aria-selected={
                    title.toLocaleLowerCase() ===
                    hoverFocusedSuggestion.toLocaleLowerCase()
                  }
                >
                  <span>{title.substring(0, title.search(value))}</span>
                  <span className={classes.matchedText}>
                    {title.substr(title.search(value), inputValue.length)}
                  </span>
                  <span>
                    {title.substring(title.search(value) + inputValue.length)}
                  </span>
                </button>
              );
            })}
          </ul>
        );
      } else {
        suggestionsListComponent = (
          <div
            className={`noMatches ${classes.suggestions}`}
            aria-label="No matches found"
          >
            <span className={classes.noMatches}>No matches</span>
          </div>
        );
      }
    }

    return (
      <ClickOutsideListener clickOutside={clickOutside}>
        <div className={classes.container} data-testid="container">
          <div
            className={classes.inputGroup}
            tabIndex={0}
            onKeyDown={onKeyUp}
            aria-label={placeholder}
            aria-owns={`suggestions-menu-${
              isStartingPoint ? "starting-point" : "destination"
            }`}
            aria-activedescendant={hoverFocusedSuggestion}
            ref={(ref) => {
              if (directionsRef) directionsRef.current = ref; //eslint-disable-line
            }}
          >
            <input
              type="combobox"
              ref={(ref) => {
                // Need to check as the unit test won't pass a reference.  Pluse disable the linter here.  It doesn't like the ref being assigned
              if (inputRef) inputRef.current = ref; //eslint-disable-line
              }}
              onFocus={onFocus}
              onChange={onChange}
              value={inputValue}
              placeholder={placeholder}
              className={`autoComplete-test-class ${classes.input}`}
              data-testid="textInput"
              aria-owns={`suggestions-menu-${
                isStartingPoint ? "starting-point" : "destination"
              }`}
              aria-activedescendant={hoverFocusedSuggestion}
            />
            <button
              className={classes.dropDownButton}
              onClick={onDropdownClick}
              tabIndex={-1}
            >
              <CaretDown className={classes.caret} />
            </button>
            {(suggestionMenu || dropDown) && suggestionsListComponent}
          </div>
        </div>
      </ClickOutsideListener>
    );
  },
  areEqualShallow
);

export default Autocomplete;
